/* eslint-disable no-case-declarations */
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { DataField } from "@iventis/domain-model/model/dataField";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { Status } from "@iventis/domain-model/model/status";
import { findNode, TreeBrowserStateInterface } from "@iventis/tree-browser";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { ViewByStatus } from "@iventis/map-engine/src/types/layer-view-by";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { IDataFieldListItemService } from "@iventis/datafield-editor";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { canLayerHaveWhat3WordsAttribute } from "@iventis/what3words";
import { createMapFromArray } from "@iventis/utilities";
import { mappingApi } from "@iventis/api/src/api";
import { map } from "./state/map.slice";
import { dispatch, rootState } from "./state/root.store";
import { convertNodeTreeDates } from "./services/library-api-functions";

export const setViewByObject = async (mapLayerId: string, treeBrowserContext: TreeBrowserStateInterface<Node>, tree: Node) => {
    const mapLayer: DomainLayer = rootState().mapReducer.mapModule.layers.value.find((lyr) => lyr.id === mapLayerId);
    if (mapLayer.viewBy !== ViewByStatus.OBJECT) {
        const updatedLayer = { ...mapLayer, viewBy: ViewByStatus.OBJECT, viewById: null };
        dispatch(map.actions.updateMapLayer(updatedLayer as DomainLayer));
        const layerNode = findNode(tree.childNodes, mapLayer.id);
        treeBrowserContext.expandNode(layerNode);
    }
};

export const setViewByDataField = async (
    datafield: DataField,
    mapLayer: DomainLayer,
    treeBrowserContext: TreeBrowserStateInterface<Node>,
    tree: Node,
    dataFieldListItemsService: IDataFieldListItemService
) => {
    // Set the layers' childNodes to be data field list items of layer and expand
    const layerNode = findNode(tree.childNodes, mapLayer.id);
    const listItems = await dataFieldListItemsService.getDataFieldListItems(datafield.id);
    const dataFieldListItemsAsNodes = listItems.map(
        ({ id, name }) =>
            ({
                id,
                name,
                type: NodeType.DataFieldListItem,
                childNodes: [],
                parentId: layerNode.sourceId,
                authorisation: Authorisation.Read,
            } as Node)
    );
    const newLayerNode = { ...layerNode, childNodes: dataFieldListItemsAsNodes };
    treeBrowserContext.updateNodeLocally(newLayerNode, undefined, true, true);
    treeBrowserContext.expandNode(newLayerNode, true);
    // Set the viewby status to DATA_FIELD
    const updatedLayer = { ...mapLayer, viewBy: ViewByStatus.DATA_FIELD, viewById: datafield.id };
    dispatch(map.actions.updateMapLayer(updatedLayer as DomainLayer));
};

export const convertLayerToBasicNode = (layer: MapLayer, overrides?: Partial<Node>): Node => {
    const node: Node = {
        id: layer.id,
        childNodes: [],
        type: NodeType.Layer,
        name: layer.name,
        favourite: false,
        sourceId: layer.id,
        authorisation: Authorisation.Admin,
        expanded: false,
        status: Status.Active,
        parentId: layer.mapId,
        childCount: undefined,
        createdByUserName: layer.createdByUserName,
        createdByUserId: layer.createdByUserId,
        lastUpdatedAt: layer.lastUpdatedAt,
        lastUpdatedByUserId: layer.lastUpdatedByUserId,
        lastUpdatedByUserName: layer.lastUpdatedByUserName,
        createdAt: layer.createdAt,
        ...overrides,
    };
    convertNodeTreeDates(node);
    return node;
};

export const setLayerThumbnail = async (layer: MapLayer, previewDataUrl: string, readonlyUser: boolean) => {
    const { id: layerId, mapId } = layer;

    const base64 = previewDataUrl.replace("data:image/png;base64,", "");

    // If it is a readonly user they can't update the thumbnail therefore resolve the promise so we can set the thumbnail in the store
    if (readonlyUser) {
        return Promise.resolve();
    }

    return mappingApi.put(`/maps/${mapId}/layers/${layerId}/preview-image`, `"${base64}"`, {
        headers: { "Content-Type": "application/json" },
    });
};

export const orderUniqueDataFieldsAcrossLayers = (layers: MapLayer[]) => {
    const dataFieldToLayersMapping = new Map<string, MapLayer[]>();
    const dataFields = layers.reduce<DataField[]>((acc, l) => {
        const layerDataFieldsToAdd = [];
        l.dataFields.forEach((df) => {
            const existing = dataFieldToLayersMapping.get(df.id);
            if (existing) {
                dataFieldToLayersMapping.set(df.id, [...existing, l]);
            } else {
                dataFieldToLayersMapping.set(df.id, [l]);
                layerDataFieldsToAdd.push(df);
            }
        });
        return [...acc, ...layerDataFieldsToAdd];
    }, []);

    // Separate datafields into system, shared and unique
    const { systemDataFields, sharedDataFields, uniqueDataFields } = dataFields.reduce<{
        systemDataFields: DataField[];
        sharedDataFields: DataField[];
        uniqueDataFields: DataField[];
    }>(
        (acc, dataField) => {
            if (dataField.systemDataFieldName != null) {
                return { ...acc, systemDataFields: [...acc.systemDataFields, dataField] };
            }
            // If data field has more than one layer, we know it's shared
            if (dataFieldToLayersMapping.get(dataField.id).length > 1) {
                return { ...acc, sharedDataFields: [...acc.sharedDataFields, dataField] };
            }
            return { ...acc, uniqueDataFields: [...acc.uniqueDataFields, dataField] };
        },
        { systemDataFields: [], sharedDataFields: [], uniqueDataFields: [] }
    );

    // Sort system by order or alphabetically
    // eslint-disable-next-line eqeqeq
    systemDataFields.sort((a, b) => (a.order == b.order ? a.name.localeCompare(b.name) : a.order == null ? 1 : b.order == null ? -1 : a.order - b.order));
    // Sort shared alphabetically
    sharedDataFields.sort((a, b) => a.name.localeCompare(b.name));
    // Sort unique by layer order, then by datafield order
    uniqueDataFields.sort((a, b) => {
        // Because they're unique to each layer, we can safely assume the data fiel only has one layer in the list, thus we take the first element
        const layerA = dataFieldToLayersMapping.get(a.id)[0];
        const layerB = dataFieldToLayersMapping.get(b.id)[0];

        if (layerA?.id !== layerB?.id) {
            return layerA?.sidebarOrder - layerB?.sidebarOrder;
        }

        return a.order - b.order;
    });
    return [...systemDataFields, ...sharedDataFields, ...uniqueDataFields];
};

/** Returns the valid project datafields depending on what layers are selected */
export const getValidProjectDataFields = (projectDataFields: DataField[], selectedLayers: DomainLayer[]) => {
    const isEveryLayerPointBased = selectedLayers.every((layer) => canLayerHaveWhat3WordsAttribute(layer));
    // Get all unique datafields from the selected layers
    const alreadyAddedDataFields = selectedLayers.reduce<DataField[]>((dataFields, layer) => {
        layer.dataFields?.forEach((layerDataField) => {
            if (!dataFields.some(({ id }) => id === layerDataField.id)) {
                dataFields.push(layerDataField);
            }
        });
        return dataFields;
    }, []);

    // Create a map with the datafield id as key, to easily check if a datafield has been added
    const alreadyAddedDataFieldsMap = createMapFromArray(alreadyAddedDataFields, "id");

    const validProjectDataFields: DataField[] = [];
    projectDataFields.forEach((df) => {
        // Datafield has not been been added yet
        if (alreadyAddedDataFieldsMap.get(df.id) == null && df.systemDataFieldName == null) {
            // If the datafield is what3words need an additional check to see if all layers are point based
            if (df.type === DataFieldType.What3Words) {
                if (isEveryLayerPointBased) {
                    validProjectDataFields.push(df);
                }
            } else {
                validProjectDataFields.push(df);
            }
        }
    });

    return validProjectDataFields;
};
